import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  addDays,
  format,
  isSameDay,
  isSameMonth,
  isPast,
  differenceInCalendarDays,
  setHours,
} from 'date-fns';
import { Droppable } from 'react-beautiful-dnd';
import getWeekIndexFromDay from '../../utils/getWeekIndexFromDay';
import createCalendarPostContext from '../hooks/createCalendarPostContext';
import AddButton from '../AddButton';
import { PostItem } from '../PostItem';
import { Day, PostsWrapper, ShowMoreButton, DayContent } from './style';

const Days = ({
  startDate,
  endDate,
  monthStart,
  weekStartsOn,
  data,
  postLimit,
  isViewOnly,
  setOpenModal,
}) => {
  const { hasPermissions } = useContext(createCalendarPostContext);

  const dateFormat = 'd';

  const dayCells = () => {
    const days = [];
    const numberDaysToDisplay =
      Math.abs(differenceInCalendarDays(startDate, endDate)) + 1;

    // Generates number of days between start and end date
    [...Array(numberDaysToDisplay)].forEach((_, i) => {
      // Posts data
      const interval = data?.calendar?.days[i];
      const day = addDays(startDate, i);
      const middayHour = setHours(day, 12);
      const middayTimestamp = middayHour.getTime() / 1000;
      const currentTimestamp = new Date().getTime() / 1000;
      const defaultTimeslot =
        currentTimestamp > middayTimestamp ? currentTimestamp : middayTimestamp;
      const rowIndex = getWeekIndexFromDay({
        currentDate: monthStart,
        day,
        weekStartsOn,
      });
      const limit = postLimit?.postsLimit?.[rowIndex];
      const isCurrentDay = isSameDay(new Date(), day);
      const isPastDay = isPast(day);
      const showAddButton = (!isPastDay || isCurrentDay) && hasPermissions;
      const inLastTwoRows = i >= numberDaysToDisplay - 14;

      days.push(
        <Droppable
          key={day.getTime().toString()}
          droppableId={day.getTime().toString()}
          isDropDisabled={isPastDay && !isCurrentDay}
        >
          {(provided, snapshot) => (
            <Day
              data-testid={`monthly-day-${day}`}
              dayNumber={format(day, dateFormat)}
              isPastMonth={!isSameMonth(day, monthStart)}
              isPast={isPastDay && !isCurrentDay}
              isActive={isCurrentDay}
              isDraggingOver={snapshot.isDraggingOver}
            >
              {showAddButton && !snapshot.isDraggingOver && (
                <AddButton
                  data-testid={`post-button-${day}`}
                  labelDate={format(day, 'eeee, d MMMM y')}
                  defaultTimeslot={defaultTimeslot}
                />
              )}
              <DayContent
                ref={provided.innerRef}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...provided.droppableProps}
              >
                {interval && (
                  <>
                    <PostsWrapper>
                      {interval?.posts.map((post, index) => {
                        if (index >= limit) {
                          return <></>;
                        }
                        return (
                          <PostItem
                            small
                            index={index}
                            key={post.id}
                            postId={post.id}
                            isViewOnly={isViewOnly}
                            inLastTwoRows={inLastTwoRows}
                            isDraggingOver={snapshot.isDraggingOver}
                            scheduledDay={format(day, 'd MMMM EEEE yyyy')}
                            setOpenModal={setOpenModal}
                          />
                        );
                      })}
                    </PostsWrapper>
                    {interval?.posts?.length > limit &&
                      !snapshot.isDraggingOver && (
                        <ShowMoreButton
                          type="text"
                          label="+ More"
                          onClick={() => postLimit?.showMorePosts(rowIndex)}
                        />
                      )}
                  </>
                )}
                {provided.placeholder}
              </DayContent>
            </Day>
          )}
        </Droppable>
      );
    });
    return days;
  };

  return dayCells();
};

Days.propTypes = {
  startDate: PropTypes.instanceOf(Date).isRequired,
  endDate: PropTypes.instanceOf(Date).isRequired,
  monthStart: PropTypes.instanceOf(Date).isRequired,
  setOpenModal: PropTypes.func,
};

Days.defaultProps = {
  setOpenModal: null,
};

export default Days;
